import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { PHOTOS } from "@/constants/photos";
import { katrinaBio } from "@/brand/content";
import { SiteHeader5 } from "@/components/layout/SiteHeader5";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { InteriorHero } from "@/components/common/InteriorHero";
import { ConsultationBanner } from "@/components/common/ConsultationBanner";

/**
 * AboutPage — Katrina's full biography (interior template).
 *
 * Route: /concept-e/about
 */
export default function AboutPage() {
  return (
    <main
      data-testid="page-about"
      className="min-h-screen"
      style={{
        background: C.surfaceLight,
        color: C.textOnLight,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <SiteHeader5 />

      <InteriorHero
        breadcrumb={[
          { label: "Faith 1st", href: "/concept-e" },
          { label: "About" },
        ]}
        eyebrow={katrinaBio.eyebrow}
        title={
          <>
            Katrina Bernard,
            <br />
            JD&nbsp;|&nbsp;CPC.
          </>
        }
        intro={katrinaBio.intro}
        meta={[
          ["Firm", "Faith 1st Consulting"],
          ["Location", "Washington, D.C. area"],
          ["Reach", "United States"],
          ["Est.", "Faith 1st Practice"],
        ]}
      />

      {/* Philosophy — tagline as first statement */}
      <section
        data-testid="about-philosophy"
        style={{
          background: C.surfaceLight,
          borderTop: `1px solid ${C.brassSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-10"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[10.5px] uppercase font-semibold"
                  style={{
                    color: C.accentMetal,
                    letterSpacing: "0.28em",
                  }}
                >
                  Philosophy
                </p>
              </div>
            </div>
            <p
              className="lg:col-span-9 font-cormorant italic"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(1.65rem, 3vw, 2.4rem)",
                fontWeight: 400,
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              {katrinaBio.philosophy}
            </p>
          </div>
        </div>
      </section>

      {/* Portrait + narrative */}
      <section
        data-testid="about-narrative"
        style={{ background: C.surfaceLight2 }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-24 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-14">
            <div className="lg:col-span-5">
              <figure className="relative sticky top-32">
                <div
                  className="relative p-3"
                  style={{
                    background: C.supportOrganic,
                    border: `1px solid ${C.brassSoft}`,
                  }}
                >
                  <div
                    className="relative overflow-hidden"
                    style={{ aspectRatio: "4 / 5", background: C.bgDark }}
                  >
                    <img
                      src={PHOTOS.contemplative}
                      alt="Katrina Bernard, Founder & Principal of Faith 1st"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{
                        objectPosition: "center 22%",
                        filter: "saturate(0.82) contrast(1.05)",
                      }}
                      loading="eager"
                    />
                  </div>
                </div>
              </figure>
            </div>
            <div className="lg:col-span-7 space-y-8">
              <p
                className="font-inter text-[11px] uppercase font-semibold pb-3"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                  borderBottom: `1px solid ${C.borderSoft}`,
                }}
              >
                Practice narrative
              </p>
              {katrinaBio.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="font-inter leading-[1.75]"
                  style={{
                    color: C.inkOnLight,
                    fontSize: "clamp(1.02rem, 1.1vw, 1.1rem)",
                  }}
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brought in when — institutional trust module */}
      <section
        data-testid="about-brought-in-when"
        style={{
          background: C.surfaceLight,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-4 mb-6">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-14"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Brought in when
                </p>
              </div>
              <h2
                className="font-cormorant"
                style={{
                  color: C.textOnLight,
                  fontSize: "clamp(1.7rem, 2.4vw, 2.1rem)",
                  fontWeight: 400,
                  lineHeight: 1.15,
                  letterSpacing: "-0.008em",
                }}
              >
                The situations Faith 1st is built for.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <ul>
                {katrinaBio.broughtInWhen.map((item, i) => (
                  <li
                    key={item}
                    data-testid={`brought-in-${i}`}
                    className="py-6 grid grid-cols-12 gap-4"
                    style={{
                      borderTop:
                        i === 0 ? `1px solid ${C.borderSoft}` : "none",
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <span
                      className="col-span-2 font-cormorant tabular-nums"
                      style={{
                        color: C.accentMetal,
                        fontSize: "1.4rem",
                        lineHeight: 1,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p
                      className="col-span-10 font-inter leading-[1.65]"
                      style={{ color: C.inkOnLight, fontSize: "1rem" }}
                    >
                      {item}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials + affiliations */}
      <section
        data-testid="about-credentials"
        style={{
          background: C.surfaceLight,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-8">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-14"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Credentials
                </p>
              </div>
              <ul>
                {katrinaBio.credentials.map(([label, sub], i) => (
                  <li
                    key={label}
                    className="py-5 grid grid-cols-12 gap-4"
                    style={{
                      borderTop:
                        i === 0 ? `1px solid ${C.borderSoft}` : "none",
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <span
                      className="col-span-2 font-inter text-[10px] uppercase font-bold tabular-nums"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.28em",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="col-span-6">
                      <p
                        className="font-cormorant"
                        style={{
                          color: C.textOnLight,
                          fontSize: "1.15rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {label}
                      </p>
                    </div>
                    <div className="col-span-4 text-right">
                      <p
                        className="font-inter text-[12px] uppercase font-semibold"
                        style={{
                          color: C.accentFunctional,
                          letterSpacing: "0.18em",
                        }}
                      >
                        {sub}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5 lg:pl-6">
              <div className="flex items-center gap-4 mb-8">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-14"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Affiliations
                </p>
              </div>
              <ul>
                {katrinaBio.affiliations.map((a, i) => (
                  <li
                    key={a}
                    className="py-4 flex items-baseline gap-4"
                    style={{
                      borderTop:
                        i === 0 ? `1px solid ${C.borderSoft}` : "none",
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <span
                      className="font-inter text-[10px] uppercase font-bold tabular-nums shrink-0"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.28em",
                      }}
                    >
                      —
                    </span>
                    <span
                      className="font-inter"
                      style={{
                        color: C.inkOnLight,
                        fontSize: "0.98rem",
                        lineHeight: 1.5,
                      }}
                    >
                      {a}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#"
                data-testid="download-cv-link"
                className="group mt-10 inline-flex items-center gap-2 pb-1 font-inter text-[11px] uppercase font-semibold border-b transition-colors"
                style={{
                  color: C.accentMetal,
                  borderColor: C.accentMetal,
                  letterSpacing: "0.26em",
                }}
              >
                Download full CV
                <ArrowRight
                  size={12}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ConsultationBanner />
      <SiteFooter
        stamp="Concept 05"
        labelSuffix="About"
        markSubtitle="Leadership, People & Culture"
        descriptor="Faith 1st is a Leadership, People & Culture Consulting firm helping mission-driven and values-led organizations navigate high-stakes people moments through consulting, training, coaching, and facilitation."
      />
    </main>
  );
}
